import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { sendAiMessage } from 'slices/aiSlice'

import { useAskMdtHotkey } from '../hooks/useAskMdtHotkey'
import { useRouteAssetContext } from '../hooks/useRouteAssetContext'

/**
 * Ask MDT's open/close state (MYD-621), after the ChatPopupContext pattern: a
 * tiny context, so the header trigger re-renders twice a session and not on
 * every streamed word. The conversation itself lives in Redux (`state.ai`).
 *
 *   closed   nothing showing
 *   palette  the Cmd/Ctrl+K prompt dialog
 *   panel    the side sheet with the conversation
 *
 * It also holds the answer in flight, so closing the panel mid-answer stops
 * it: the request is cancelled and the half-written answer is kept as
 * "stopped" (aiSlice).
 *
 * Mounted only when Ask MDT is available to this user (availability.ts). The
 * trigger and the shortcut read `useAskMdt()`, which is null outside it, so
 * they simply do not appear.
 */

export type AskMdtMode = 'closed' | 'palette' | 'panel'

interface AskMdtContextValue {
	mode: AskMdtMode
	openPalette: () => void
	openPanel: () => void
	close: () => void
	/** The header button: open the panel, or close whatever is open. */
	toggle: () => void
	/** Send a prompt and show the panel. */
	ask: (text: string) => void
	/** Stop the answer streaming now, keeping what arrived. */
	stop: () => void
}

const AskMdtContext = React.createContext<AskMdtContextValue | null>(null)

/** Ask MDT's controls, or null when it is not offered to this user. */
export const useAskMdt = () => useContext(AskMdtContext)

export const AskMdtProvider = ({ children }: { children: React.ReactNode }) => {
	const dispatch = useDispatch<any>()
	const { pathname } = useLocation()
	const routeContext = useRouteAssetContext()
	const [mode, setMode] = useState<AskMdtMode>('closed')
	const inFlight = useRef<{ abort: () => void } | null>(null)

	const stop = useCallback(() => {
		inFlight.current?.abort()
		inFlight.current = null
	}, [])

	const close = useCallback(() => {
		stop()
		setMode('closed')
	}, [stop])

	const openPalette = useCallback(() => setMode('palette'), [])
	const openPanel = useCallback(() => setMode('panel'), [])
	const toggle = useCallback(() => (mode === 'closed' ? setMode('panel') : close()), [mode, close])

	const ask = useCallback(
		(text: string) => {
			const prompt = text.trim()
			if (!prompt) return
			setMode('panel')
			// The screen asked from (MYD-622), sent with EVERY message, null
			// included: moving to a page about nothing in particular must clear
			// the last page's asset, not leave "this project" pointing at it.
			const request = dispatch(sendAiMessage({ text: prompt, routeContext }))
			inFlight.current = request
			Promise.resolve(request).finally(() => {
				if (inFlight.current === request) inFlight.current = null
			})
		},
		[dispatch, routeContext],
	)

	// The shortcut opens the palette, or closes Ask MDT when it is open.
	const onHotkey = useCallback(() => (mode === 'closed' ? setMode('palette') : close()), [mode, close])
	useAskMdtHotkey(onHotkey, pathname)

	const value = useMemo(() => ({ mode, openPalette, openPanel, close, toggle, ask, stop }), [mode, openPalette, openPanel, close, toggle, ask, stop])
	return <AskMdtContext.Provider value={value}>{children}</AskMdtContext.Provider>
}

export default AskMdtProvider
